// 집중 — 방해 자극 속에서 한 가지에만 주의를 붙잡아 두는 연습.
//  · 기호 세기: 비슷하게 생긴 기호(ㅂ/ㅃ/ㅍ, b/d/p/q …)가 섞인 판에서 목표 기호의 개수를 센다
//  · 색 글자(스트룹): '빨강'이라고 쓰인 파란 글자 → 글자의 뜻이 아니라 색을 고른다
import type { ChoiceQuestion } from './ChoiceDrill';
import { int, makeRng, pick, shuffle, type Rng } from './rng';

export type FocusQuestion = ChoiceQuestion & { options: string[] } & (
  | { kind: 'count'; grid: string[]; cols: number; target: string }
  | { kind: 'stroop'; word: string; ink: number }
);

export const STROOP_COLORS: [string, string][] = [['빨강', '#e53935'], ['파랑', '#1e63d6'], ['초록', '#1f9d55'], ['노랑', '#d4a400']];
export const GLYPHS: string[][] = [['ㅂ', 'ㅃ', 'ㅍ', 'ㅁ'], ['b', 'd', 'p', 'q'], ['6', '9', '8', '0'], ['을', '울', '올', '율'], ['◁', '▷', '◀', '▶'], ['E', 'F', 'B', 'P'], ['몽', '뭉', '멍', '망']];

function countQ(r: Rng, lv: number): FocusQuestion {
  const cols = Math.min(10, 5 + Math.ceil(lv / 2)), rows = Math.min(8, 3 + Math.ceil(lv / 2));
  const set = pick(r, GLYPHS);
  const glyphs = set.slice(0, lv <= 3 ? 2 : lv <= 6 ? 3 : 4);
  const target = glyphs[0];
  const cells = cols * rows;
  const k = int(r, Math.round(cells * 0.18), Math.round(cells * 0.35));
  const grid = shuffle(r, [...Array(k).fill(target), ...Array.from({ length: cells - k }, () => pick(r, glyphs.slice(1)))]);
  const start = k - int(r, 0, 4);
  const options = Array.from({ length: 5 }, (_, i) => String(start + i));
  return { kind: 'count', grid, cols, target, options, answer: options.indexOf(String(k)), n: 5, limitSec: Math.max(15, Math.round(cells * 0.55)), explain: `'${target}'은(는) ${k}개. 요령: 한 줄씩 손가락 대신 눈으로 짚고, 줄마다 개수를 더해 간다.` };
}

function stroopQ(r: Rng, lv: number): FocusQuestion {
  const ink = int(r, 0, 3);
  const word = r() < 0.8 ? pick(r, STROOP_COLORS.filter((_, i) => i !== ink))[0] : STROOP_COLORS[ink][0];
  return { kind: 'stroop', word, ink, options: STROOP_COLORS.map((c) => c[0]), answer: ink, n: 4, fast: true, limitSec: Math.round((3.6 - 0.2 * (lv - 1)) * 10) / 10 };
}

export function makeFocusRound(level: number, seed: number): FocusQuestion[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const counts = Array.from({ length: lv <= 3 ? 3 : 4 }, () => countQ(rng, lv));
  const stroops = Array.from({ length: 8 }, () => stroopQ(rng, lv));
  // 기호 세기 사이사이에 색 글자 묶음을 끼운다
  const out: FocusQuestion[] = [];
  counts.forEach((c, i) => {
    out.push(c);
    out.push(...stroops.slice(i * 2, i * 2 + (i === counts.length - 1 ? 8 : 2)));
  });
  return out.slice(0, counts.length + 8);
}
